<script setup lang="ts">
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { TTooltipAlignment, TTooltipDirection } from '@lifk/types'
import { PhInfo } from '@phosphor-icons/vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** What text content should appear inside the tooltip? */
    tooltipText?: string
    /** What accessible label should show for screen readers if no visible tooltip text is provided? */
    ariaLabel?: string
    /** In what direction should the tooltip extend away from the target? */
    tooltipDirection: TTooltipDirection
    /** How should the tooltip be aligned relative to the target? */
    tooltipAlignment: TTooltipAlignment
    /** What should the maximum width of the tooltip be? */
    tooltipMaxWidthPx?: number
    /** What should the size of the info icon be in pixels? */
    size?: number
    /** Should there be a delay when rolling off of the target element that prevents the tooltip from hiding right away? (Any value triggers the delay.) */
    delayTag?: string
    /** Should the toolitip appear on mobile devices? */
    visibleOnMobile?: boolean
    /** What size should the text be? */
    textSize?: 'auto' | 'small' | 'default'
  }>(),
  {
    tooltipMaxWidthPx: 365,
    size: 16,
    visibleOnMobile: false,
    textSize: 'default'
  }
)

const hovered = ref(false)

// Strip HTML tags from tooltip text for aria-label
// We could consider putting this in a utility function like strings and math
const cleanAriaLabel = computed(() => {
  if (props.ariaLabel) return props.ariaLabel
  if (!props.tooltipText) return undefined

  return props.tooltipText
    .replace(/<[^>]*>/g, '') // Remove html tags
    .replace(/\s+/g, ' ') //  whitespace
    .trim()
})
</script>

<template>
  <UIToolTip
    :class="[{ 'hidden sm:flex': !visibleOnMobile }, { flex: visibleOnMobile }]"
    :direction="tooltipDirection"
    :alignment="tooltipAlignment"
    :text="tooltipText"
    :max-width-px="tooltipMaxWidthPx"
    :delay-tag="delayTag"
    :text-size="textSize"
  >
    <button
      type="button"
      class="flex-shrink-0 bg-transparent border-none p-0 cursor-pointer"
      tabindex="0"
      :aria-label="cleanAriaLabel"
      @mouseover="hovered = true"
      @mouseleave="hovered = false"
    >
      <PhInfo
        :size="size"
        class="fill-slate-tint-1"
        :weight="hovered ? 'fill' : 'regular'"
      />
    </button>
    <div v-if="$slots.tooltipContent">
      <slot name="tooltipContent" />
    </div>
  </UIToolTip>
</template>
